<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTagEditor } from '@/composables/useTagEditor'
import TagNameInput from '@/components/todos/TagNameInput.vue'
import { chipStyle } from '@/todos/tags'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

// The tags a tag adds to every todo that has it: one row each with a remove button, a field for a new one with suggestions of
// the user's tags.
const props = defineProps<{ tagId: number }>()
const { t } = useI18n()
const editor = useTagEditor()
const { impliesDrafts } = editor
const tag = computed(() => editor.tags.value.find((existing) => existing.id === props.tagId))

async function pick(name: string): Promise<void> {
  if (!tag.value) return
  impliesDrafts[tag.value.id] = name
  await editor.addImplication(tag.value)
}
</script>

<template>
  <div v-if="tag && !tag.internal" class="tag-implied" :data-tag-id="tag.id">
    <UiGroup>
      <p class="crumb">
        <span class="label" :style="chipStyle(tag.color)"># {{ tag.name }}</span>
      </p>
    </UiGroup>
    <UiGroup :footer="t('settings.tags.impliesHint')">
      <UiList class="suggest">
        <UiRow v-for="implied in tag.implies" :key="implied.id">
          <span class="entry-title"># {{ implied.name }}</span>
          <button
            type="button"
            class="icon"
            :aria-label="t('common.remove')"
            @click="editor.removeImplication(tag, implied.tag_id)"
          >
            ✕
          </button>
        </UiRow>
        <UiRow>
          <TagNameInput
            v-model="impliesDrafts[tag.id]"
            class="alias-input"
            :names="editor.impliable(tag)"
            :placeholder="t('settings.tags.impliesPlaceholder')"
            :aria-label="t('settings.tags.implies')"
            @pick="pick"
            @submit="editor.addImplication(tag)"
            @blur="editor.addImplication(tag)"
          />
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
  <p v-else-if="editor.loaded.value" class="ui-hint">{{ t('settings.tags.gone') }}</p>
  <p v-else>{{ t('common.loading') }}</p>
</template>

<style scoped>
.crumb {
  margin: 0;
}

.label {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
}

.entry-title {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.ui-list.suggest {
  overflow: visible;
}

.alias-input {
  flex: 1;
  min-width: 0;
}

.icon {
  width: auto;
  padding: 0.1rem 0.4rem;
  background: none;
  color: inherit;
  border: none;
  cursor: pointer;
}
</style>
